import { z } from 'zod';

/**
 * Type enum for `AccountTransaction` entries.
 *
 * Replaces the broker-era `customer_*` / `carrier_*` prefixed
 * transaction types with leg-aware, role-neutral names that fit the
 * network model (any party can be buyer or seller; no "Hero pays
 * carrier" framing).
 *
 * ## Per-leg invoice lifecycle (the common path)
 *
 * - `leg_capture` — seller captures earnings for a delivered leg;
 *   creates the leg invoice and bumps the Account balance by
 *   `+invoice.total` from `parties[0]`'s POV (sign depends on
 *   whether the seller is `parties[0]` or `parties[1]`).
 * - `leg_payment` — buyer pays seller against one or more invoices.
 *   Gateway REQUIRED. Pulls balance back toward zero.
 * - `leg_void` — reversal of a capture (cancel after capture). Posts
 *   the inverse of the original capture's deltas so balance is
 *   restored.
 * - `leg_adjustment` — recapture delta when the invoice amount
 *   changes post-capture (e.g. fee added or removed).
 * - `advance_payment` — buyer prepays before any invoice. Held as
 *   credit (`unusedAmount`) on a synthetic advance-payment invoice;
 *   future captures draw it down via `unusedAmountChange`.
 *
 * ## Direct ledger moves (reused names from current Transaction)
 *
 * Used by admin / finance flows where there's no order leg:
 * - `account_debit` — admin debits a party's account.
 * - `account_credit` — admin credits a party's account.
 * - `account_debit_payment_failure` — debit reversal after a
 *   payment gateway failure (mirror of the existing
 *   `wallet_debit_payment_failure` semantics).
 * - `refund` — refund moves money out of the seller's pocket.
 * - `cashback` — promotional credit.
 */
export const AccountTransactionTypeSchema = z.enum([
  // Per-leg invoice lifecycle:
  'leg_capture',
  'leg_payment',
  'leg_void',
  'leg_adjustment',
  'advance_payment',

  // Direct ledger moves:
  'account_debit',
  'account_credit',
  'account_debit_payment_failure',
  'refund',
  'cashback',
]);
export type AccountTransactionType = z.infer<typeof AccountTransactionTypeSchema>;
